import React, { useState, useEffect, useRef } from 'react';
import { usePendingChangesStore } from '@/store/pendingChangesStore';
import type { DispatchedKeys } from '@/store/pendingChangesStore';
import { Button } from './Button';
import { debug } from '@/utils/debug';

// Animation duration in ms - must match Tailwind's slide-up/slide-down animation (0.4s)
const SLIDE_ANIMATION_DURATION_MS = 400;

interface SaveChangesBarProps {
  /** Optional callback after successful save */
  onSaveSuccess?: () => void | Promise<void>;
  /** Optional callback after save failure */
  onSaveError?: (errors: string[]) => void;
  /** Optional: Override change detection (default: uses pendingChangesStore) */
  hasChanges?: () => boolean;
  /** Optional: Override change count (default: uses pendingChangesStore) */
  getChangeCount?: () => number;
  /** Optional: Override change summary (default: uses pendingChangesStore) */
  getChangeSummary?: () => string[];
  /** Optional: Custom save handler (default: uses pendingChangesStore.saveAllChanges).
   *  When the custom handler internally calls the pendingChangesStore's
   *  saveAllChanges, it should forward the `dispatched` field through so
   *  the bar can target the post-save clear to only those keys (leaving
   *  any post-snapshot queue additions intact). When the handler doesn't
   *  touch the pendingChangesStore (e.g. pure app-config save), omit
   *  `dispatched` and the bar will skip the clear entirely. */
  onSave?: () => Promise<{
    success: boolean;
    errors: string[];
    dispatched?: DispatchedKeys;
  }>;
  /** Optional: Custom discard handler (default: uses pendingChangesStore callbacks + clear) */
  onDiscard?: () => void;
  /** Optional: External saving state to combine with internal state */
  isExternallySaving?: boolean;
}

/**
 * SaveChangesBar Component
 *
 * A sticky bar that appears at the bottom of the screen when there are pending changes.
 * Shows a summary of pending changes and Save/Discard buttons.
 *
 * Can be used in two modes:
 * 1. Simple mode: Just uses pendingChangesStore (default)
 * 2. Extended mode: Pass custom props for change detection, save, and discard
 *
 * Usage (Simple):
 * ```tsx
 * <SaveChangesBar
 *   onSaveSuccess={() => toast.success('Changes saved!')}
 *   onSaveError={(errors) => errors.forEach(e => toast.error(e))}
 * />
 * ```
 *
 * Usage (Extended - for Account page with multiple stores):
 * ```tsx
 * <SaveChangesBar
 *   hasChanges={() => hasPendingChanges || hasBudgetChanges}
 *   getChangeCount={() => pendingCount + budgetCount}
 *   getChangeSummary={() => [...pendingSummary, ...budgetSummary]}
 *   onSave={handleCombinedSave}
 *   onDiscard={handleCombinedDiscard}
 *   isExternallySaving={isSavingBudgets}
 *   onSaveSuccess={() => toast.success('All changes saved!')}
 * />
 * ```
 */
export function SaveChangesBar({
  onSaveSuccess,
  onSaveError,
  hasChanges: customHasChanges,
  getChangeCount: customGetChangeCount,
  getChangeSummary: customGetChangeSummary,
  onSave: customOnSave,
  onDiscard: customOnDiscard,
  isExternallySaving = false,
}: SaveChangesBarProps) {
  const {
    hasChanges: storeHasChanges,
    getChangeCount: storeGetChangeCount,
    getChangeSummary: storeGetChangeSummary,
    isSaving: storeIsSaving,
    saveAllChanges,
    clearAllChanges,
    clearDispatchedChanges,
    executeDiscardCallbacks,
  } = usePendingChangesStore();

  const [isExiting, setIsExiting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use custom functions if provided, otherwise use store functions
  const hasChanges = customHasChanges ?? storeHasChanges;
  const getChangeCount = customGetChangeCount ?? storeGetChangeCount;
  const getChangeSummary = customGetChangeSummary ?? storeGetChangeSummary;
  const isSaving = storeIsSaving || isExternallySaving;

  const hasActiveChanges = hasChanges();

  // Handle visibility with exit animation
  useEffect(() => {
    if (hasActiveChanges) {
      // Cancel any pending exit animation if changes are re-added
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
      setIsExiting(false);
      setIsVisible(true);
    } else if (!hasActiveChanges && isVisible && !isExiting) {
      // Trigger exit animation when changes return to zero
      setIsExiting(true);
      exitTimeoutRef.current = setTimeout(() => {
        setIsExiting(false);
        setIsVisible(false);
      }, SLIDE_ANIMATION_DURATION_MS);
    }
    // Note: isVisible and isExiting are intentionally excluded from deps.
    // - isVisible: to avoid potential loops (we read it to check if exit animation is needed)
    // - isExiting: only used as a guard condition, doesn't need to trigger the effect
    // Only hasActiveChanges changes should trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveChanges]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
    };
  }, []);

  // Don't render if not visible and no changes
  if (!isVisible && !hasActiveChanges) {
    return null;
  }

  const changeCount = getChangeCount();
  const summary = getChangeSummary();

  const handleSave = async () => {
    // No-op if there are no active changes. The bar can remain mounted
    // during the 400ms exit animation after the queue has already been
    // drained (visibility useEffect's slide-down setTimeout); a click
    // in that window would otherwise call into customOnSave/saveAllChanges
    // and fire onSaveSuccess (toast/refetch) despite there being nothing
    // to save.
    if (!hasActiveChanges) {
      return;
    }

    // Run the targeted clearDispatchedChanges safety net FIRST, then
    // run onSaveSuccess (parent's refetch + success toast). The order
    // matters: if we deferred the clear into a `finally` after the
    // refetch (often slow — Account/Campaign pages can refetch for
    // 10+ seconds), the user could queue a fresh edit for the same
    // key during that window, and the safety-net clear's key-only
    // delete would wipe their post-save edit. Running first ensures
    // any mid-refetch re-queue is preserved.
    //
    // saveAllChanges' bulk drop has already filtered `dispatched`
    // down to keys whose live Map entry was identity-equal to the
    // snapshot, so in practice this clear is a no-op safety net for
    // saveAllChanges-driven calls. Keeping it for robustness against
    // future callers that might construct `dispatched` differently.
    //
    // onSaveSuccess is wrapped in its own try/catch so a refetch
    // failure can't propagate out of runPostSaveWork — the underlying
    // save succeeded, and the parent already had a chance to clean
    // up state via the synchronous clear above.
    const runPostSaveWork = async (dispatched?: DispatchedKeys) => {
      if (dispatched) {
        clearDispatchedChanges(dispatched);
      }
      try {
        await onSaveSuccess?.();
      } catch (error) {
        debug.error('Error in SaveChangesBar onSaveSuccess:', error);
      }
    };

    // Run the post-save work synchronously on the same tick as the
    // save's resolution — NOT inside a setTimeout (the prior
    // triggerExit approach). The visibility useEffect would
    // clearTimeout that timer the moment a new change was queued
    // (so it can keep the bar visible), which had the side effect
    // of killing the post-save work — onSaveSuccess + clearDispatched
    // would silently skip on any quick follow-up edit. Running inline
    // removes the timer; the visibility useEffect handles the slide-
    // down animation on its own when hasActiveChanges drops false.
    //
    // The outer try/catch catches throws from customOnSave /
    // saveAllChanges themselves — without it, a network blowup or
    // unexpected exception inside the save path would become an
    // unhandled promise rejection and the parent would never see
    // an error via onSaveError.
    try {
      if (customOnSave) {
        const result = await customOnSave();
        if (result.success) {
          await runPostSaveWork(result.dispatched);
        } else {
          onSaveError?.(result.errors);
        }
      } else {
        const result = await saveAllChanges();
        if (result.success) {
          await runPostSaveWork(result.dispatched);
        } else {
          onSaveError?.(result.errors);
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      debug.error('SaveChangesBar: save threw unexpectedly', error);
      onSaveError?.([message]);
    }
  };

  const handleDiscard = () => {
    // No-op if there are no active changes. Same window as Save above:
    // during the visibility useEffect's 400ms slide-down, the bar can
    // still be mounted with hasActiveChanges already false, and a
    // Discard click would otherwise run executeDiscardCallbacks +
    // clearAllChanges (or customOnDiscard) for an empty queue.
    if (!hasActiveChanges) {
      return;
    }

    // Run the discard work synchronously on click. Previously this was
    // wrapped in triggerExit, which deferred the actual state mutation
    // until the 400ms slide-down animation finished. During that window
    // any input the user committed (e.g. typing a new budget into a
    // newly-focused field) would queue a fresh pending change, and the
    // late-firing cascade would then silently wipe it — looking like
    // "optimistic update failed" even though Discard was working as
    // designed. Running synchronously makes the click commit-point and
    // the state mutation atomic; the bar's visibility useEffect then
    // animates the exit independently as hasActiveChanges flips false.
    // Wrap in try/catch: a parent-supplied customOnDiscard (or a
    // registered discard callback) could throw, and now that the
    // discard runs synchronously inside the click handler — no
    // longer behind triggerExit's try/catch — an unhandled throw
    // would propagate up to React's click dispatch and risk a
    // visible UI break. Log and swallow so one misbehaving caller
    // can't take down the SaveChangesBar.
    try {
      if (customOnDiscard) {
        customOnDiscard();
      } else {
        executeDiscardCallbacks();
        clearAllChanges();
      }
    } catch (error) {
      debug.error('Error in SaveChangesBar discard handler:', error);
    }
  };

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 text-gray-800 shadow-[0_-8px_30px_rgba(0,0,0,0.3)] border-t border-gray-200 bg-white ${isExiting ? 'animate-slide-down' : 'animate-slide-up'}`}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-1 sm:gap-2">
        {/* Left side: Change summary */}
        <div className="flex items-center gap-1.5 sm:gap-4">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-flouri-pink text-white text-xs sm:text-sm font-bold flex-shrink-0">
              {changeCount}
            </span>
            <span className="font-medium text-xs sm:text-base whitespace-nowrap">
              {changeCount === 1 ? 'Unsaved change' : 'Unsaved changes'}
            </span>
          </div>

          {/* Summary badges */}
          <div className="hidden sm:flex items-center gap-2">
            {summary.map((item, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-gray-200 rounded text-sm text-gray-700"
              >
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Right side: Actions */}
        <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
          <Button
            variant="ghost"
            onClick={handleDiscard}
            disabled={isSaving}
            className="!text-gray-600 hover:!text-gray-800 hover:!bg-gray-200 !px-2 sm:!px-4 !text-xs sm:!text-sm"
          >
            Discard
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving}
            className="!bg-flouri-pink !text-white hover:!bg-primary-600 !px-2 sm:!px-4 !text-xs sm:!text-sm"
          >
            {isSaving ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
